import { COURTOIS_URL } from '@/config'
import type { DastNode, Update, UpdateKind } from '../types'

// The marketing site serves the changelog as JSON from its committed content
// snapshot (website-v2 /api/changelog). One fetch covers the list, the detail
// pages, prev/next navigation, and the unread dot, so the promise is cached
// for the session.

interface ChangelogEntry {
  id: string
  slug: string
  title: string
  tag: string | null
  date: string | null
  firstPublishedAt: string | null
  body: { document: DastNode } | null
}

const toKind = (raw: string | null): UpdateKind => {
  const value = (raw ?? '').toLowerCase()
  if (value.includes('improve')) return 'improvement'
  if (value.includes('fix')) return 'fix'
  return 'feature'
}

const plainText = (node: DastNode): string =>
  node.type === 'span'
    ? (node.value ?? '')
    : (node.children ?? []).map(plainText).join('')

const firstParagraph = (document?: DastNode): string => {
  const paragraph = document?.children?.find(
    (node) => node.type === 'paragraph',
  )
  return paragraph ? plainText(paragraph).trim() : ''
}

const toUpdate = (entry: ChangelogEntry): Update => {
  const document = entry.body?.document
  return {
    id: entry.id,
    slug: entry.slug,
    kind: toKind(entry.tag),
    title: entry.title,
    summary: firstParagraph(document),
    publishedAt: entry.date ?? entry.firstPublishedAt ?? '',
    body: document,
  }
}

let entriesPromise: Promise<ChangelogEntry[]> | null = null

const fetchEntries = (): Promise<ChangelogEntry[]> => {
  entriesPromise ??= fetch(`${COURTOIS_URL}/api/changelog`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`changelog request failed (${response.status})`)
      }
      const payload = (await response.json()) as { entries: ChangelogEntry[] }
      return payload.entries
    })
    .catch((error) => {
      entriesPromise = null
      throw error
    })
  return entriesPromise
}

export async function fetchUpdates(): Promise<Update[]> {
  const entries = await fetchEntries()
  return entries.map(toUpdate)
}

export async function fetchUpdate(slug: string): Promise<Update | null> {
  const entries = await fetchEntries()
  const entry = entries.find((candidate) => candidate.slug === slug)
  return entry ? toUpdate(entry) : null
}

export interface UpdateLink {
  slug: string
  title: string
}

export interface UpdateNav {
  previous: UpdateLink | null
  next: UpdateLink | null
}

export async function fetchUpdateNav(slug: string): Promise<UpdateNav> {
  const entries = await fetchEntries()
  const index = entries.findIndex((entry) => entry.slug === slug)
  if (index === -1) {
    return { previous: null, next: null }
  }
  return {
    previous: entries[index + 1] ?? null,
    next: entries[index - 1] ?? null,
  }
}

export async function fetchLatestPublishedAt(): Promise<string | null> {
  const entries = await fetchEntries()
  const latest = entries[0]
  return latest ? (latest.date ?? latest.firstPublishedAt) : null
}
